import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, CheckCircle2, Undo2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { dateShort, INVOICE_STYLES } from "@/lib/format";
import { todayYmd } from "@/lib/csv";

const TERMS = [["receipt", "Due on receipt"], ["net15", "Net 15"], ["net30", "Net 30"], ["net45", "Net 45"], ["net60", "Net 60"]];

export const InvoiceStatusPill = ({ inv, testId }) => {
  const label = inv.overdue ? "Overdue" : inv.status;
  return <span className={`pill ${INVOICE_STYLES[label]}`} data-testid={testId}>{inv.overdue && <AlertTriangle className="w-3 h-3" />}{label}</span>;
};

export const DraftOptions = ({ inv, onPatch }) => (
  <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="invoice-draft-options">
    <div>
      <Label className="text-xs text-slate-500">Remit-to</Label>
      <div className="mt-1 flex rounded-full bg-slate-100 p-0.5">
        {[["direct", "Direct"], ["factored", "Factored"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => k !== inv.remit_profile && onPatch({ remit_profile: k })} aria-pressed={inv.remit_profile === k}
            className={`px-3 py-1 rounded-full text-sm ${inv.remit_profile === k ? "bg-white shadow-sm font-semibold" : "text-slate-500"}`} data-testid={`invoice-remit-${k}`}>{l}</button>
        ))}
      </div>
    </div>
    <div>
      <Label htmlFor="inv-terms" className="text-xs text-slate-500">Payment terms</Label>
      <select id="inv-terms" value={inv.terms} onChange={(e) => onPatch({ terms: e.target.value })} className="mt-1 block h-9 rounded-md border border-input bg-white px-2 text-sm" data-testid="invoice-terms-select">
        {TERMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
    </div>
    <p className="text-xs text-slate-500 flex-1 min-w-[180px]">These lock once you mark the invoice as sent.</p>
  </div>
);

export const StatusActions = ({ inv, onPatch }) => {
  const [paying, setPaying] = useState(false);
  const [paidAt, setPaidAt] = useState(todayYmd());
  const [busy, setBusy] = useState(false);
  const run = async (body) => { setBusy(true); try { await onPatch(body); setPaying(false); } finally { setBusy(false); } };
  return (
    <>
      {inv.status === "Draft" && (
        <Button onClick={() => run({ status: "Sent" })} disabled={busy} className="bg-[#002FA7] hover:bg-[#001A75] rounded-full" data-testid="mark-sent-btn"><Send className="w-4 h-4 mr-1" /> Mark as Sent</Button>
      )}
      {inv.status === "Sent" && (
        <Button onClick={() => { setPaidAt(todayYmd()); setPaying(true); }} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="mark-paid-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Mark as Paid</Button>
      )}
      {inv.status === "Paid" && (
        <>
          <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="paid-on"><CheckCircle2 className="w-3 h-3" /> Paid {dateShort(`${inv.paid_at}T12:00:00`)}</span>
          <Button variant="ghost" size="sm" onClick={() => run({ status: "Sent" })} disabled={busy} data-testid="mark-unpaid-btn"><Undo2 className="w-4 h-4 mr-1" /> Mark unpaid</Button>
        </>
      )}
      <Dialog open={paying} onOpenChange={(v) => !v && !busy && setPaying(false)}>
        <DialogContent className="max-w-sm" data-testid="mark-paid-dialog">
          <DialogHeader><DialogTitle>Mark invoice {inv.invoice_number} paid</DialogTitle></DialogHeader>
          <div><Label htmlFor="paid-at">Date paid</Label><Input id="paid-at" type="date" min={inv.sent_on || undefined} max={todayYmd()} value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="mt-1" data-testid="paid-at-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaying(false)} disabled={busy}>Cancel</Button>
            <Button onClick={() => run({ status: "Paid", paid_at: paidAt })} disabled={!paidAt || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-paid-btn">Mark paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const MissingSettingsNote = ({ inv }) => (!inv.company?.name || !inv.remit_text) && inv.status === "Draft" ? (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="invoice-settings-note">
    {!inv.company?.name ? "Your company details aren't set up yet, so this invoice shows a placeholder." : `No ${inv.remit_profile} remit-to details yet.`}{" "}
    <Link to="/admin/settings" className="font-semibold underline">Open Settings</Link>
  </div>
) : null;
